import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchEvents, deleteEvent, duplicateEvent } from '../../store/slices/eventSlice';
import { addNotification } from '../../store/slices/uiSlice';
import EventModal from './EventModal';
import CourseEnrollmentModal from './CourseEnrollmentModal';
import { requireEventAccess } from '../common/SecureAdminPage';
import actionBtn from '../common/actionButtonStyles';

const EventDataTable = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { events = [], loading = false, error = null } = useSelector(state => state.events) || {};
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('worship'); // 'worship', 'course', 'cell_group', 'meeting', 'event'
  
  // Date filter state - separate input values from applied filter values
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    console.log('🔄 EventDataTable: Dispatching fetchEvents...');
    dispatch(fetchEvents());
  }, [dispatch]);

  // Ensure events is always an array
  const eventsArray = Array.isArray(events) ? events : [];

  // Debug logging
  console.log('🔍 EventDataTable state:', { events, loading, error });
  console.log('🔍 EventDataTable - events:', events, 'type:', typeof events, 'isArray:', Array.isArray(events));
  console.log('🔍 EventDataTable - eventsArray length:', eventsArray.length);

  // Filter events based on active tab (event_type), search term, and date range
  const filteredEvents = eventsArray.filter(event => {
    // Filter by event type (tab)
    const eventType = event?.event_type || 'event';
    const matchesTab = eventType === activeTab;
    
    // Filter by search term
    const matchesSearch = event?.event_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event?.venue && event.venue.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event?.main_lecturer && event.main_lecturer.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event?.sections && event.sections.some(section => 
        (section?.section_name && section.section_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (section?.lecturer && section.lecturer.toLowerCase().includes(searchTerm.toLowerCase()))
      ));
    
    // Filter by date range (check if any section falls within the date range)
    let matchesDate = true;
    if (startDate || endDate) {
      const sections = event?.sections || [];
      if (sections.length === 0) {
        matchesDate = false;
      } else {
        matchesDate = sections.some(section => {
          const sectionDate = section?.start_at ? new Date(section.start_at) : null;
          if (!sectionDate) return false;
          
          const sectionDateOnly = new Date(sectionDate.getFullYear(), sectionDate.getMonth(), sectionDate.getDate());
          
          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            return sectionDateOnly >= start && sectionDateOnly <= end;
          } else if (startDate) {
            const start = new Date(startDate);
            return sectionDateOnly >= start;
          } else if (endDate) {
            const end = new Date(endDate);
            return sectionDateOnly <= end;
          }
          return true;
        });
      }
    }
    
    return matchesTab && matchesSearch && matchesDate;
  });

  // Count events by type for tab badges
  const eventCounts = {
    worship: eventsArray.filter(e => (e?.event_type || 'event') === 'worship').length,
    course: eventsArray.filter(e => (e?.event_type || 'event') === 'course').length,
    cell_group: eventsArray.filter(e => (e?.event_type || 'event') === 'cell_group').length,
    meeting: eventsArray.filter(e => (e?.event_type || 'event') === 'meeting').length,
    event: eventsArray.filter(e => (e?.event_type || 'event') === 'event').length,
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredEvents.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentEvents = filteredEvents.slice(startIndex, endIndex);
  
  // Reset to first page when search, tab, or date filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, startDate, endDate]);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleCreateEvent = () => {
    setSelectedEvent({ event_type: activeTab }); // Pass the current tab as event_type
    setIsCreateMode(true);
    setShowEventModal(true);
  };

  const handleManageEvent = (event) => {
    setSelectedEvent(event);
    setIsCreateMode(false);
    setShowEventModal(true);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
    setShowEventModal(false);
  };

  const handleModalSuccess = () => {
    dispatch(fetchEvents()); // Refresh the data
  };

  const handleDeleteEvent = async (event) => {
    if (window.confirm(t('events.confirmations.deleteMessage', { name: event.event_name }))) {
      try {
        await dispatch(deleteEvent(event.id)).unwrap();
        dispatch(addNotification({
          type: 'success',
          title: t('common.successTitle'),
          message: t('events.notifications.deleteSuccess')
        }));
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          title: t('common.errorTitle'),
          message: error.message || t('events.notifications.error', { action: t('events.actions.delete').toLowerCase() })
        }));
      }
    }
  };

  const handleDuplicateEvent = async (event) => {
    if (window.confirm(t('events.confirmations.duplicateMessage', { name: event.event_name }))) {
      try {
        await dispatch(duplicateEvent(event.id)).unwrap();
        dispatch(addNotification({
          type: 'success',
          title: t('common.successTitle'),
          message: t('events.notifications.duplicateSuccess')
        }));
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          title: t('common.errorTitle'),
          message: error.message || t('events.notifications.error', { action: t('events.actions.duplicate').toLowerCase() })
        }));
      }
    }
  };

  const handleManageEnrollment = (event) => {
    setSelectedCourse(event);
    setShowEnrollmentModal(true);
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to get lecturer information
  const getLecturerInfo = (event) => {
    // Check if there's a main lecturer
    if (event.main_lecturer && event.main_lecturer.trim()) {
      return event.main_lecturer.trim();
    }
    
    // Check if sections have lecturers
    if (event.sections && event.sections.length > 0) {
      const lecturers = event.sections
        .map(section => section.lecturer)
        .filter(lecturer => lecturer && lecturer.trim())
        .join(', ');
      
      if (lecturers) {
        return lecturers;
      }
    }
    
    return '-';
  };  if (loading && eventsArray.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">{t('events.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && eventsArray.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{t('events.error')}</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <button 
              onClick={() => dispatch(fetchEvents())} 
              className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              {t('events.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg">
      {/* Security Notice */}
      <div className="px-6 py-4 bg-green-50 border-b border-green-200">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-green-800 text-sm font-medium">
            🔒 {t('events.secureNotice')}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="flex -mb-px min-w-max" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('worship')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'worship'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="hidden sm:inline">{t('events.tabs.worship')} </span>
              <span className="sm:hidden">{t('events.tabs.worship')}</span>
              <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'worship' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {eventCounts.worship}
              </span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('course')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'course'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              {t('events.tabs.course')}
              <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'course' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {eventCounts.course}
              </span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('event')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'event'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {t('events.tabs.event')}
              <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'event' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {eventCounts.event}
              </span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('cell_group')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'cell_group'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {t('events.tabs.cellGroup')}
              <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'cell_group' ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {eventCounts.cell_group}
              </span>
            </span>
          </button>
          <button
            onClick={() => setActiveTab('meeting')}
            className={`px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'meeting'
                ? 'border-teal-500 text-teal-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {t('events.tabs.meeting')}
              <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 text-xs rounded-full ${
                activeTab === 'meeting' ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-600'
              }`}>
                {eventCounts.meeting}
              </span>
            </span>
          </button>
        </nav>
      </div>

      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t(`events.management.${activeTab === 'cell_group' ? 'cellGroup' : activeTab}`)}
          </h3>
          <div className="flex items-center space-x-4 flex-wrap gap-y-2">
            {/* Date Range Filter */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                <label className="text-sm text-gray-500 mr-2">{t('events.filters.from')}</label>
                <input
                  type="date"
                  value={startDateInput}
                  onChange={(e) => setStartDateInput(e.target.value)}
                  onBlur={(e) => setStartDate(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && setStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div className="flex items-center">
                <label className="text-sm text-gray-500 mr-2">{t('events.filters.to')}</label>
                <input
                  type="date"
                  value={endDateInput}
                  onChange={(e) => setEndDateInput(e.target.value)}
                  onBlur={(e) => setEndDate(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && setEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              {(startDateInput || endDateInput) && (
                <button
                  onClick={() => { setStartDateInput(''); setEndDateInput(''); setStartDate(''); setEndDate(''); }}
                  className="px-2 py-2 text-gray-500 hover:text-gray-700 text-sm"
                  title={t('events.filters.clearFilter')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <div>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                className="pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value={5}>{t('events.filters.perPage', { count: 5 })}</option>
                <option value={10}>{t('events.filters.perPage', { count: 10 })}</option>
                <option value={25}>{t('events.filters.perPage', { count: 25 })}</option>
                <option value={50}>{t('events.filters.perPage', { count: 50 })}</option>
              </select>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder={t(`events.filters.search${activeTab === 'worship' ? 'Worships' : activeTab === 'course' ? 'Courses' : activeTab === 'cell_group' ? 'CellGroups' : activeTab === 'meeting' ? 'Meetings' : 'Events'}`)}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              onClick={handleCreateEvent}
              className={`${
                activeTab === 'worship' ? 'bg-blue-600 hover:bg-blue-700' : 
                activeTab === 'course' ? 'bg-green-600 hover:bg-green-700' : 
                activeTab === 'cell_group' ? 'bg-orange-600 hover:bg-orange-700' :
                activeTab === 'meeting' ? 'bg-teal-600 hover:bg-teal-700' :
                'bg-purple-600 hover:bg-purple-700'
              } text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center`}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              {t('events.actions.create', { 
                type: t(`events.tabs.${activeTab === 'cell_group' ? 'cellGroup' : activeTab}`)
              })}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('events.table.id')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('events.table.eventName')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('events.table.venue')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('events.table.lecturer')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('events.table.sections')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('events.table.nextSection')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('events.table.created')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('events.table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentEvents.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  {searchTerm ? t('events.table.noMatch') : t('events.table.noEvents')}
                </td>
              </tr>
            ) : (
              currentEvents.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {event.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{event.event_name}</div>
                    {event.remark && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">{event.remark}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {event.venue || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="max-w-xs truncate">
                      {getLecturerInfo(event)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {event.sections_count || event.sections?.length || 0} {t('events.table.sections')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(event.next_section || (event.sections?.[0]?.start_at))}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateTime(event.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleManageEnrollment(event)}
                        className={actionBtn.enroll}
                      >
                        參加者名單
                      </button>
                      <button
                        onClick={() => handleManageEvent(event)}
                        className={actionBtn.manage}
                      >
                        {t('events.actions.manage')}
                      </button>
                      <button
                        onClick={() => handleDuplicateEvent(event)}
                        className={actionBtn.duplicate}
                        title={t('events.actions.duplicate')}
                      >
                        {t('events.actions.duplicate')}
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(event)}
                        className={actionBtn.delete}
                      >
                        {t('events.actions.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer - Always show when there are items */}
      {filteredEvents.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t('events.pagination.showing', { 
                from: startIndex + 1, 
                to: Math.min(endIndex, filteredEvents.length), 
                total: filteredEvents.length 
              })}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('events.pagination.previous')}
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {[...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return (
                    <span key={pageNum} className="px-2 py-2 text-gray-500">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('events.pagination.next')}
            </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showEventModal && (
        <EventModal
          event={selectedEvent}
          isCreateMode={isCreateMode}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
        />
      )}

      {showEnrollmentModal && selectedCourse && (
        <CourseEnrollmentModal
          event={selectedCourse}
          onClose={() => {
            setShowEnrollmentModal(false);
            setSelectedCourse(null);
          }}
        />
      )}
    </div>
  );
};

// Export with security wrapper requiring event access
// TEMPORARILY DISABLED FOR DEBUGGING
export default EventDataTable;
// export default requireEventAccess(EventDataTable);
